import * as React from 'react'
import { cn } from '@/lib/utils'

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  hint?: string
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, label, error, hint, id, ...props }, ref) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-')
    const errorId = error ? `${inputId}-error` : undefined
    const hintId = hint && !error ? `${inputId}-hint` : undefined
    const describedBy = errorId || hintId

    const input = (
      <input
        type={type}
        id={inputId}
        ref={ref}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={describedBy}
        className={cn(
          'flex h-10 w-full rounded-lg border bg-transparent px-3 py-2 text-sm transition-colors duration-150 ease-out',
          'border-[var(--border-default)] text-[var(--text-primary)]',
          'placeholder:text-[var(--text-muted)]',
          'focus:border-[var(--text-primary)] focus:outline-none',
          'disabled:cursor-not-allowed disabled:bg-[var(--bg-elevated)] disabled:text-[var(--text-muted)]',
          'file:border-0 file:bg-transparent file:text-sm file:font-medium',
          error && 'border-[var(--error)] focus:border-[var(--error)]',
          className
        )}
        {...props}
      />
    )

    if (!label && !error && !hint) {
      return input
    }

    return (
      <div className="w-full space-y-1.5">
        {label && (
          <label
            htmlFor={inputId}
            className="text-sm font-medium text-[var(--text-primary)]"
          >
            {label}
          </label>
        )}
        {input}
        {hint && !error && (
          <p id={hintId} className="text-sm text-[var(--text-secondary)]">
            {hint}
          </p>
        )}
        {error && (
          <p id={errorId} role="alert" className="text-sm text-[var(--error)]">
            {error}
          </p>
        )}
      </div>
    )
  }
)
Input.displayName = 'Input'

export { Input }
